import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'
import type { DeviceType } from '~/types/device-type'

import ExternalLinkItem from './ExternalLinkItem'

import { getDeviceTypes, getDeviceTypeSwitchButtons } from '../common'

import AppLogo from '~/components/AppLogo'
import ModalAppSelector, { AppSelectorDropdown } from '~/components/AppSelector'
import Badge from '~/components/Badge'
import LinkButton from '~/components/Buttons/LinkButton'
import SwitchButton from '~/components/Buttons/SwitchButton'
import MwMLoader from '~/components/MwMLoader'
import Tooltip from '~/components/Tooltip'
import { FASTLY_PATH } from '~/const/asset'
import { shortcut } from '~/const/shortcut'
import { useShortcut } from '~/hooks/useShortcut'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useExternalLinksListQuery } from '~/queries/external-links'
import { useVersionsListQueryV2 } from '~/queries/versions'
import { getExternalStoreId, getExternalStoreLink, transformByDeviceType } from '~/utils/application'
import dayjs from '~/utils/dayjs'
import { resize } from '~/utils/media'

import '../common.scss'
import './styles.scss'

const HeaderSummary = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { appDetails, appKey, deviceType, isLoading } = useAppSummary()

  const iconUrl = appDetails?.icon_file_id

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [isAppSelectorModalOpen, setIsAppSelectorModalOpen] = useState(false)

  const [isSwitchDisabled, setIsSwitchDisabled] = useState(true)

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([])

  const deviceTypes = useMemo(() => getDeviceTypes(appDetails), [appDetails])

  /** Handlers  */

  const handleSwitchButtonSelectionChange = (buttonId: string) => {
    const selectedButton = switchButtons.find(({ id }) => id === buttonId)

    setSwitchButtons(
      switchButtons.map((button) => ({
        ...button,
        isActive: button.id === buttonId,
      }))
    )

    if (selectedButton?.application) {
      dispatchToSearchParams({ type: 'update', value: { app: selectedButton.application } })
    }
  }

  /** Queries & Mutations */

  const { data: versionsData } = useVersionsListQueryV2({
    deviceType,
    externalId: appDetails ? getExternalStoreId(appDetails, deviceType) : undefined,
    limit: 3,
  })

  /** Effects */

  useShortcut({
    ...shortcut.openAppSelector,
    fn: () => {
      const modal = document.querySelector('.ReactModal__Body--open')
      if (!modal) {
        setIsAppSelectorModalOpen(true)
      }
    },
  })

  useEffect(() => {
    setSwitchButtons(getDeviceTypeSwitchButtons({ appKey, currentDeviceType: deviceType, deviceTypes }))
  }, [appKey, deviceType, deviceTypes])

  useEffect(() => {
    setIsSwitchDisabled(deviceTypes.length < 2)
  }, [deviceTypes])

  /** Queries & Mutations */

  const { data: externalLinks } = useExternalLinksListQuery(appKey, deviceType as DeviceType)

  const externalLinksGroupClassnames = classNames('external-links', 'group', { 'no-data': externalLinks?.length === 0 })

  /** Handlers */

  const handleRedirect = (path: string, internal?: boolean) => () =>
    internal ? navigate(path) : window.open(path, '_blank')

  const handleOpenModal = (value: boolean) => () => setIsAppSelectorModalOpen(value)

  /** Render */

  if (isLoading)
    return (
      <div className="loader-container">
        <MwMLoader category="item" />
      </div>
    )

  if (!appDetails) return <div className="not-found-container">App not found</div>

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const lastVersion = versionsData?.data?.[0]

  return (
    <div className="header-component header-summary">
      <div className="app-infos-container">
        <AppLogo
          appLogoUrl={iconUrl ? resize(`${FASTLY_PATH}${iconUrl}`, 250, 250) : undefined}
          appPlatform={deviceType}
          appUrl={getExternalStoreLink(appDetails, deviceType)}
          borderColor="#272727"
          darkBackground
          size="large"
        />
        <div className="data-container">
          <div className="row1">
            <AppSelectorDropdown apps={appSelectorApp ? [appSelectorApp] : []} onClick={handleOpenModal(true)} />
            <ModalAppSelector
              isOpen={isAppSelectorModalOpen}
              onClose={handleOpenModal(false)}
              selectedApps={[{ app_key: appKey, device_type: deviceType }]}
            />
            <SwitchButton
              backgroundColor="dark"
              buttons={switchButtons}
              disabled={isSwitchDisabled}
              onButtonSelectionChange={handleSwitchButtonSelectionChange}
              size="medium"
            />
          </div>
          <div className="row2">
            <div className="version group">
              <div className="section-title">Default version</div>
              <div>
                <div className="version-and-tag">
                  <div className="last-version">{lastVersion?.semantic_version || 'No data'}</div>
                  <LinkButton
                    label="Go to configuration"
                    onClick={handleRedirect(`/apps-management/${appDetails.app_key}`)}
                    type="internal"
                  />
                </div>
                <div className="release-date">
                  {lastVersion?.release_date
                    ? `${dayjs(lastVersion.release_date).fromNow()} - ${formatDate(
                        lastVersion.release_date,
                        DATE_FORMATS.DEFAULT_WITH_HOURS
                      )}`
                    : ''}
                </div>
              </div>
            </div>
            {externalLinks && externalLinks.length > 0 ? (
              <>
                <div className={externalLinksGroupClassnames}>
                  <div className="external-links-title section-title">
                    <div className="flex align-center">
                      Links <Badge className="link-count-badge" value={externalLinks.length} />
                    </div>
                    <LinkButton
                      className="see-more-link-button"
                      label="See more"
                      onClick={handleRedirect(`/external-links`, true)}
                      size="small"
                      type="internal"
                    />
                  </div>
                  <div className="links-container">
                    {externalLinks.slice(0, 8).map(({ icon, target, url }, idx) => (
                      <Tooltip
                        align={{ points: ['bl', 'tl'] }}
                        className="summary-header-links-tooltip"
                        key={idx}
                        mouseEnterDelay={0.2}
                        overlay={target}
                        placement="top"
                        trigger="hover"
                      >
                        <div className="link-container">
                          <ExternalLinkItem icon={icon} onClick={handleRedirect(url)} target={target} />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeaderSummary
